import { XIcon } from '@heroicons/react/outline';
import { Affix, Button, Modal } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { notify } from '@/components/shared/notify';

import { Endpoints } from '@/network';
import { getAllStore } from '@/services/Store';
import { IGetAllStoreQueryParams, IStore } from '@/utils/interfaces/Store';

const ShopModal = ({
  open,
  setOpen,
  isIframe = false,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  isIframe?: boolean;
}) => {
  const router = useRouter();

  const { book_id } = router.query;
  const [loading, setLoading] = useState(false);
  const screens = useBreakpoint();
  const [stores, setStore] = useState<IStore[]>([]);

  const getAllStoreHandle = async (params: IGetAllStoreQueryParams) => {
    try {
      setLoading(true);
      const result = await getAllStore(params);
      setStore(result.data.data);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const body = document.body;
    if (open) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = '';
    }

    return () => {
      body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (open && book_id) {
      getAllStoreHandle({ book: book_id as string });
    }
  }, [open, book_id]);

  const isMobile = !screens.md;
  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      onCancel={() => setOpen(false)}
      getContainer={false}
      maskClosable
      className={`${isMobile ? 'w-full h-full ' : 'border-grayZl-900 border-[1px]   rounded-lg '}`}
      classNames={{
        body: 'h-screen md:h-fit bg-grayZl-950 rounded-lg flex flex-col p-0 ',
        mask: 'bg-grayZl-950 md:bg-transparent rounded-xl',
        content: `${isMobile ? 'p-0 h-full w-full bg-black' : 'p-0 bg-grayZl-950 h-fit'}`,
        wrapper: 'hi',
      }}
      style={{
        position: !isMobile ? 'fixed' : undefined,
        top: isMobile ? 0 : stores.length > 2 ? '20%' : '30%',
        right: isMobile ? 0 : isIframe ? 112 : 110,
        padding: 0,
        width: isMobile ? '100%' : 500,
        height: isMobile ? '100%' : 'calc(100vh - 30%)',
        background: 'transparent',
        boxShadow: 'none',
      }}
    >
      <div>
        <PanelContent stores={stores} setOpen={setOpen} loading={loading} />
      </div>
    </Modal>
  );
};

const PanelContent = ({
  stores,
  setOpen,
  loading,
}: {
  stores: IStore[];
  setOpen: (val: boolean) => void;
  loading: boolean;
}) => {
  return (
    <div className='flex flex-col bg-grayZl-950 rounded-lg md:h-[80vh]'>
      <Affix offsetTop={0}>
        <div className='flex items-center justify-between px-5 py-4 border-b border-grayZl-900 bg-black rounded-t rounded-lg'>
          <h2 className='text-white text-lg font-semibold'>Store</h2>
          <button onClick={() => setOpen(false)}>
            <XIcon className='text-white w-5 h-5' />
          </button>
        </div>
      </Affix>

      <div className='overflow-y-auto flex-1 rounded-lg'>
        {stores.length === 0 && !loading && (
          <div className='h-[80vh] md:h-[20vh] flex justify-center items-center font-semibold text-white text-base'>
            No Product Found
          </div>
        )}
        {stores.map((store, idx) => (
          <a
            key={idx}
            href={store?.link}
            target='_blank'
            rel='noopener noreferrer'
          >
            <div
              className={clsx(
                stores.length < 2
                  ? 'border-2 border-t-0 rounded-lg border-x-0 border-grayZl-900'
                  : '',
                'bg-black text-white  cursor-pointer flex flex-col md:flex-row  ',
              )}
            >
              <div className='relative w-full aspect-[16/9]  md:mx-6 max-w-full min-w-full md:max-w-[172px] md:min-w-[172px] rounded-sm overflow-hidden'>
                <Image
                  src={`${process.env.NEXT_PUBLIC_API_READER_URL}${Endpoints.store.getStoreImage(store._id)}`}
                  alt={store.title}
                  fill
                  className='object-contain p-6 md:p-0'
                  sizes='(min-width: 1280px) 172px, (min-width: 640px) 250px, 100vw'
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>

              <div className='flex flex-col items-start gap-4 p-6 bg-grayZl-950'>
                <div className='flex flex-col items-start gap-2 self-stretch'>
                  <div className='text-white text-sm font-semibold'>
                    {store?.title}
                  </div>
                  <div className='text-white text-xs font-thin line-clamp-1'>
                    {store?.subtitle}
                  </div>
                  <div className='text-primary text-xs font-medium'>
                    {store?.author?.name}
                  </div>
                  <div className='flex items-center gap-2'>
                    <div className='text-white font-semibold'>
                      {store?.price ? `₹${store?.price?.toLocaleString()}` : ''}
                    </div>
                    <div className='text-[#c7c6cf] line-through'>
                      {store?.strokedPrice
                        ? `₹${store?.strokedPrice?.toLocaleString()}`
                        : ''}
                    </div>
                    {/* <div className='flex items-center gap-0.5'>
                    <div className='text-green-400'>{store.discount}</div>
                    <CourseArrowIcon />
                  </div> */}
                  </div>
                </div>
                <Button className='flex items-center w-full md:w-fit gap-2 h-10 md:h-8 rounded-lg bg-white border-none'>
                  <span className='text-black text-sm font-semibold leading-5'>
                    Know More
                  </span>
                </Button>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

export default ShopModal;
